import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Page, PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { naira, fmtDateTime } from "../lib/format";
import { AlertTriangle, Banknote, ShieldCheck, ArrowRightLeft, Check, Download, Printer } from "lucide-react";
import { downloadReport } from "./accounting/exportHelpers";

export default function Tills() {
  const [tills, setTills] = useState([]);
  const [locations, setLocations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", location_id: "", max_cash_limit: "", terminal_row: "" });
  const [positions, setPositions] = useState({});
  const [busy, setBusy] = useState(false);
  const [safe, setSafe] = useState(null);
  const [range, setRange] = useState(() => {
    const to = new Date().toISOString().slice(0, 10);
    const f = new Date(); f.setDate(f.getDate() - 30);
    return { date_from: f.toISOString().slice(0, 10), date_to: to, till: "" };
  });

  const load = () => {
    api.get("/tills").then((r) => setTills(r || [])).catch(() => {});
    api.get("/locations").then((r) => setLocations(r || [])).catch(() => {});
    api.get("/tills/alerts").then((r) => setAlerts(r || [])).catch(() => {});
    api.get("/tills/cash-movements").then((r) => setMovements(r || [])).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    (async () => {
      const map = {};
      for (const t of tills) {
        try {
          const p = await api.get("/tills/cash-position", { params: { till: t.name, location_id: t.location_id } });
          map[t.id] = p;
        } catch { /* ignore */ }
      }
      setPositions(map);
    })();
  }, [tills]);

  const save = async () => {
    if (!form.name || !form.location_id) { toast.error("Name and location are required"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        location_id: form.location_id,
        max_cash_limit: Number(form.max_cash_limit) || 0,
        terminal_row: form.terminal_row === "" ? null : Number(form.terminal_row),
        active: true,
      };
      if (form.id) {
        await api.put(`/tills/${form.id}`, body);
        toast.success(`TILL ${form.name} updated`);
      } else {
        await api.post("/tills", body);
        toast.success(`TILL ${form.name} created`);
      }
      setForm({ id: null, name: "", location_id: "", max_cash_limit: "", terminal_row: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save TILL");
    } finally { setBusy(false); }
  };

  const editRow = (t) => setForm({ id: t.id, name: t.name, location_id: t.location_id, max_cash_limit: t.max_cash_limit, terminal_row: t.terminal_row ?? "" });
  const cancelEdit = () => setForm({ id: null, name: "", location_id: "", max_cash_limit: "", terminal_row: "" });
  const deactivate = async (t) => {
    if (!window.confirm(`Deactivate ${t.name}?`)) return;
    try { await api.delete(`/tills/${t.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const loadSafe = (r = range) => api.get("/tills/safe-log", { params: { date_from: r.date_from, date_to: r.date_to, till: r.till || undefined } })
    .then(setSafe).catch(() => setSafe(null));
  useEffect(() => { loadSafe(range); /* eslint-disable-next-line */ }, [range]);

  const byLoc = useMemo(() => {
    const m = {};
    locations.forEach((l) => (m[l.id] = l.name));
    return m;
  }, [locations]);

  return (
    <Page>
      <PageHeader title="TILLs" subtitle="Configure maximum cash limits per TILL, watch live cash positions and audit cash movements" />

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="p-5" data-testid="till-form">
          <h3 className="font-brand font-semibold mb-3" style={{ color: "#3B2722" }}>{form.id ? `Edit ${form.name}` : "Create TILL"}</h3>
          <div className="space-y-3">
            <div><Label className="text-xs">TILL name</Label><Input placeholder="e.g. Till 4" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="till-name" /></div>
            <div>
              <Label className="text-xs">Branch / Location</Label>
              <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="till-location"
                className="w-full h-10 rounded-md border px-3 text-sm" style={{ borderColor: "#E9DED0", background: "#fff" }}>
                <option value="">Choose a branch…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Maximum Cash Limit (₦)</Label><Input type="number" min="0" step="1000" placeholder="e.g. 500000" value={form.max_cash_limit} onChange={(e) => setForm({ ...form, max_cash_limit: e.target.value })} data-testid="till-limit" /></div>
            <div>
              <Label className="text-xs">POS terminal row (1–10)</Label>
              <select value={form.terminal_row} onChange={(e) => setForm({ ...form, terminal_row: e.target.value })} data-testid="till-terminal-row"
                className="w-full h-10 rounded-md border px-3 text-sm" style={{ borderColor: "#E9DED0", background: "#fff" }}>
                <option value="">Not assigned</option>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{`Terminal row ${n}`}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} data-testid="till-save" className="flex-1 text-white" style={{ background: "#8B1E1E" }}>{busy ? "SAVING…" : form.id ? "UPDATE TILL" : "CREATE TILL"}</Button>
              {form.id && <Button variant="outline" onClick={cancelEdit} data-testid="till-cancel-edit">Cancel</Button>}
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="till-alerts">
          <h3 className="font-brand font-semibold mb-3 flex items-center gap-2" style={{ color: "#3B2722" }}>
            <AlertTriangle size={18} style={{ color: "#B7791F" }} /> Open cash-limit alerts
          </h3>
          {alerts.length === 0 ? (
            <div className="text-sm" style={{ color: "#a58f7c" }}>All TILLs are within their configured limits.</div>
          ) : alerts.map((a, i) => (
            <div key={a.id} data-testid={`alert-row-${i}`} className="border rounded-lg p-3 mb-2" style={{ borderColor: "#e6bcbc", background: "rgba(165,34,34,.05)" }}>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold" style={{ color: "#8B1E1E" }}>{a.till} · {a.location_name}</span>
                <span className="text-xs" style={{ color: "#a58f7c" }}>{fmtDateTime(a.created_at)}</span>
              </div>
              <div className="text-sm" style={{ color: "#3B2722" }}>
                Position <b>{naira(a.current_position)}</b> · Limit {naira(a.limit)} · <span style={{ color: "#A52222" }}>Over by {naira(a.over_by)}</span>
              </div>
              {a.cashier_name && <div className="text-xs" style={{ color: "#765A4A" }}>Cashier: {a.cashier_name}</div>}
            </div>
          ))}
        </Card>
      </div>

      <div className="rounded-xl border shadow-sm overflow-hidden mb-6" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="tills-list">
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "#F0E9DD" }}>
          <Banknote size={18} style={{ color: "#C49A4A" }} />
          <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>All TILLs</h3>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
            <th className="text-left px-5 py-3">TILL</th>
            <th className="text-left px-4 py-3">Location</th>
            <th className="text-center px-4 py-3">Terminal Row</th>
            <th className="text-right px-4 py-3">Max Cash Limit</th>
            <th className="text-right px-4 py-3">Current Position</th>
            <th className="text-center px-4 py-3">Status</th>
            <th className="text-right px-5 py-3">Actions</th>
          </tr></thead>
          <tbody>{tills.map((t, i) => {
            const pos = positions[t.id];
            const over = pos?.over_limit;
            return (
              <tr key={t.id} data-testid={`till-row-${i}`} className="border-b" style={{ borderColor: "#F5EFE5" }}>
                <td className="px-5 py-3 font-medium" style={{ color: "#3B2722" }}>{t.name}</td>
                <td className="px-4 py-3">{byLoc[t.location_id] || t.location_name}</td>
                <td className="px-4 py-3 text-center" data-testid={`till-row-terminal-${i}`}>{t.terminal_row ? `Row ${t.terminal_row}` : "—"}</td>
                <td className="px-4 py-3 text-right">{naira(t.max_cash_limit)}</td>
                <td className="px-4 py-3 text-right" style={{ color: over ? "#A52222" : "#3B2722", fontWeight: 600 }}>{pos ? naira(pos.cash_position) : "—"}</td>
                <td className="px-4 py-3 text-center">
                  {!t.active ? <span className="text-xs" style={{ color: "#a58f7c" }}>Inactive</span>
                    : over ? <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: "#F5D6D6", color: "#A52222" }}>OVER LIMIT</span>
                    : <span className="text-xs px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(79,122,82,.1)", color: "#4F7A52" }}><Check size={12} /> OK</span>}
                </td>
                <td className="px-5 py-3 text-right space-x-2">
                  <Button variant="outline" size="sm" onClick={() => editRow(t)} data-testid={`till-edit-${i}`}>Edit</Button>
                  {t.active && <Button variant="outline" size="sm" onClick={() => deactivate(t)} data-testid={`till-deactivate-${i}`}>Deactivate</Button>}
                </td>
              </tr>
            );
          })}
          {tills.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No TILLs configured yet — save one above to get started.</td></tr>}
          </tbody>
        </table></div>
      </div>

      <div className="rounded-xl border shadow-sm overflow-hidden" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="cash-movements">
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "#F0E9DD" }}>
          <ArrowRightLeft size={18} style={{ color: "#C49A4A" }} />
          <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Approved Cash Movements</h3>
          <span className="text-xs" style={{ color: "#a58f7c" }}>Post-approval events from TILL_CASH_MOVEMENT workflow</span>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
            <th className="text-left px-5 py-3">When</th>
            <th className="text-left px-4 py-3">TILL</th>
            <th className="text-left px-4 py-3">Initiated By</th>
            <th className="text-center px-4 py-3">Direction</th>
            <th className="text-right px-4 py-3">Amount</th>
            <th className="text-right px-4 py-3">Prev → New</th>
            <th className="text-left px-5 py-3">Reason</th>
          </tr></thead>
          <tbody>{movements.map((m, i) => (
            <tr key={m.id} data-testid={`movement-row-${i}`} className="border-b" style={{ borderColor: "#F5EFE5" }}>
              <td className="px-5 py-3">{fmtDateTime(m.movement_at)}</td>
              <td className="px-4 py-3 font-medium">{m.till}</td>
              <td className="px-4 py-3">{m.initiated_by_name}</td>
              <td className="px-4 py-3 text-center">
                <span className="text-xs font-bold uppercase px-2 py-1 rounded" style={{ background: m.direction === "out" ? "#F5D6D6" : "rgba(79,122,82,.15)", color: m.direction === "out" ? "#A52222" : "#4F7A52" }}>{m.direction}</span>
              </td>
              <td className="px-4 py-3 text-right font-semibold" style={{ color: "#8B1E1E" }}>{naira(m.amount)}</td>
              <td className="px-4 py-3 text-right text-xs" style={{ color: "#765A4A" }}>{naira(m.previous_position)} → {naira(m.new_position)}</td>
              <td className="px-5 py-3 text-xs" style={{ color: "#765A4A" }}>{m.reason}</td>
            </tr>
          ))}
          {movements.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No approved cash movements yet.</td></tr>}
          </tbody>
        </table></div>
      </div>
      <div className="rounded-xl border shadow-sm overflow-hidden mt-6" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="safe-log">
        <div className="px-5 py-4 border-b flex flex-wrap items-end gap-3" style={{ borderColor: "#F0E9DD" }}>
          <div className="flex items-center gap-2 mr-auto">
            <ShieldCheck size={18} style={{ color: "#C49A4A" }} />
            <div>
              <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Safe Log Book</h3>
              <span className="text-xs" style={{ color: "#a58f7c" }}>Running safe balance from approved cash movements, plus shift sweeps</span>
            </div>
          </div>
          <div><Label className="text-xs">From</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="safe-from" className="h-9 w-40" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="safe-to" className="h-9 w-40" /></div>
          <div>
            <Label className="text-xs">TILL</Label>
            <select value={range.till} onChange={(e) => setRange({ ...range, till: e.target.value })} data-testid="safe-till"
              className="h-9 rounded-md border px-3 text-sm w-40" style={{ borderColor: "#E9DED0", background: "#fff" }}>
              <option value="">All TILLs</option>
              {tills.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <Button variant="outline" size="sm" data-testid="safe-export-csv"
            onClick={() => downloadReport("/tills/exports/safe-log.csv", `safe-log-${range.date_from}_${range.date_to}.csv`, { date_from: range.date_from, date_to: range.date_to, till: range.till || undefined })}>
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <Button size="sm" className="text-white" style={{ background: "#8B1E1E" }} data-testid="safe-export-pdf"
            onClick={() => downloadReport("/tills/exports/safe-log.pdf", `safe-log-${range.date_from}_${range.date_to}.pdf`, { date_from: range.date_from, date_to: range.date_to, till: range.till || undefined })}>
            <Printer size={14} className="mr-1" /> Print PDF
          </Button>
        </div>

        {safe && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-5 py-4" data-testid="safe-totals">
            {[["Cash in", safe.totals.cash_in, "#4F7A52"], ["Cash out", safe.totals.cash_out, "#A52222"],
              ["Net movement", safe.totals.net_movement, "#3B2722"], ["Swept from tills", safe.totals.shift_cash_swept_to_safe, "#3B2722"],
              ["Cash variance", safe.totals.cash_variance, Math.abs(safe.totals.cash_variance) > 0 ? "#A52222" : "#4F7A52"]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl border px-3 py-2" style={{ background: "#FFFDF9", borderColor: "#E9DED0" }}>
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>{l}</div>
                <div className="font-brand font-bold text-lg" style={{ color: c }}>{naira(v)}</div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
            <th className="text-left px-5 py-3">When</th>
            <th className="text-left px-4 py-3">TILL</th>
            <th className="text-left px-4 py-3">Branch</th>
            <th className="text-center px-4 py-3">Direction</th>
            <th className="text-right px-4 py-3">Amount</th>
            <th className="text-right px-4 py-3">Safe Balance</th>
            <th className="text-left px-5 py-3">Reason</th>
          </tr></thead>
          <tbody>{(safe?.rows || []).map((m, i) => (
            <tr key={m.id} data-testid={`safe-row-${i}`} className="border-b" style={{ borderColor: "#F5EFE5" }}>
              <td className="px-5 py-3">{fmtDateTime(m.movement_at)}</td>
              <td className="px-4 py-3 font-medium">{m.till}</td>
              <td className="px-4 py-3">{m.location_name}</td>
              <td className="px-4 py-3 text-center">
                <span className="text-xs font-bold uppercase px-2 py-1 rounded" style={{ background: m.direction === "out" ? "#F5D6D6" : "rgba(79,122,82,.15)", color: m.direction === "out" ? "#A52222" : "#4F7A52" }}>{m.direction}</span>
              </td>
              <td className="px-4 py-3 text-right font-semibold" style={{ color: m.direction === "out" ? "#A52222" : "#4F7A52" }}>{naira(m.signed_amount)}</td>
              <td className="px-4 py-3 text-right font-semibold">{naira(m.running_balance)}</td>
              <td className="px-5 py-3 text-xs" style={{ color: "#765A4A" }}>{m.reason}</td>
            </tr>
          ))}
          {(!safe?.rows || safe.rows.length === 0) && <tr><td colSpan={7} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No cash movements in this period.</td></tr>}
          </tbody>
        </table></div>
      </div>
    </Page>
  );
}
